import type { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import type { DashboardVoyage as Voyage } from './types'

const VoyageTimeline: FC<{ activeVoyages: Voyage[]; showMissingEta?: boolean }> = ({ activeVoyages, showMissingEta = false }) => {
  const navigate = useNavigate()
  const now = Date.now()
  const valid = activeVoyages.filter(v => v.eta && Number.isFinite(Date.parse(v.eta)))
  const missing = activeVoyages.length - valid.length
  const past = valid.filter(v => Date.parse(v.eta!) < now).length
  const later = valid.filter(v => Date.parse(v.eta!) > now + 7 * 86400000).length
  const voyages = valid.filter(v => Date.parse(v.eta!) >= now && Date.parse(v.eta!) <= now + 7 * 86400000)
    .sort((a, b) => Date.parse(a.eta!) - Date.parse(b.eta!))
  const undated = showMissingEta ? activeVoyages.filter(v => !v.eta || !Number.isFinite(Date.parse(v.eta))) : []
  const rows = [...voyages, ...undated].slice(0, 5)
  // A rolling seven-day window can intersect eight local calendar dates.
  const days = Array.from({ length: 8 }, (_, i) => {
    const date = new Date(now)
    date.setHours(0, 0, 0, 0)
    date.setDate(date.getDate() + i)
    return date
  })
  const columns = '76px repeat(8, minmax(26px, 1fr))'
  return <div className='card flex-1' style={{ borderRadius: '16px' }}><div className='card-body'>
    <div className='d-flex align-items-center gap-2 mb-3'>
      <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '13px' }}>Voyage Schedule</h6>
      <span className='text-muted' style={{ fontSize: '10px' }}>(Next 7 Days)</span>
    </div>
    <div className='overflow-auto'>
      <div style={{ minWidth: '292px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: columns }} className='align-items-center mb-3 gap-1'>
          <span aria-hidden='true' />
          {days.map((date, i) => <div key={date.toDateString()} className={`text-center rounded py-2 ${i === 0 ? 'bg-primary text-white' : 'text-muted'}`}>
            <div style={{ fontSize: '9px' }}>{date.toLocaleDateString(undefined, { month: 'short' })}</div>
            <div className='fw-semibold' style={{ fontSize: '11px' }}>{date.getDate()}</div>
          </div>)}
        </div>
        {rows.length > 0 && <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>{rows.map(v => {
          const hasEta = Boolean(v.eta && Number.isFinite(Date.parse(v.eta)))
          const eta = hasEta ? new Date(v.eta!) : null
          const day = eta ? days.findIndex(date => date.toDateString() === eta.toDateString()) : 0
          const position = eta ? (day + (eta.getHours() * 60 + eta.getMinutes()) / 1440) / days.length * 100 : 0
          return <li key={v.id} className='d-flex align-items-center gap-2'>
            <div className='text-muted flex-shrink-0 text-break' style={{ width: '76px', fontSize: '10px' }}>
              <div>{v.vessel?.fleet_name || 'Vessel name unavailable'}</div>
              <div style={{ fontSize: '9px' }}>{v.voyageNumber}</div>
            </div>
            <div className='flex-1 position-relative' style={{ height: '18px' }}>
              {/* The pill annotates an arrival point; its label width is not a voyage duration. */}
              <div className={`position-absolute d-flex align-items-center px-1 rounded border ${hasEta ? 'border-primary bg-light-primary text-primary' : 'bg-light text-muted'}`}
                style={{ left: `${position}%`, width: `${Math.min(65, 100 - position)}%`, height: '100%', overflow: 'hidden' }}
                title={eta ? `Arrival ETA: ${eta.toLocaleString()} · ${v.arrivalPort || 'Port not reported'}` : v.eta ? 'ETA invalid' : 'ETA not set'}>
                {eta && <span className='rounded-circle bg-primary flex-shrink-0' aria-hidden='true' style={{ width: 6, height: 6 }} />}
                <span className='text-truncate ms-1' style={{ fontSize: '9px' }}>{eta ? `ETA - ${v.arrivalPort || 'Port not reported'}` : v.eta ? 'ETA invalid' : 'ETA not set'}</span>
                {eta && <><span className='visually-hidden'>ETA</span><time className='visually-hidden' dateTime={v.eta}>{eta.toLocaleString()}</time></>}
              </div>
            </div>
          </li>
        })}</ul>}
      </div>
    </div>
    {rows.length === 0 && <div className='text-muted py-3' style={{ fontSize: '11px' }}>No saved arrival ETAs fall within the next 7 days.</div>}
    {rows.length > 0 && <div className='d-flex flex-wrap gap-3 mt-3 pt-3 border-top' style={{ fontSize: '9px' }}>
      <span className='text-muted'><span className='text-primary me-1' aria-hidden='true'>●</span>Arrival ETA</span>
      {undated.length > 0 && <span className='text-muted'>ETA not set / invalid</span>}
    </div>}
    <div className='d-flex flex-wrap gap-2 text-muted mt-3' style={{ fontSize: '9px' }}>
      <span>{activeVoyages.length} active voyages</span>
      <span>{missing} missing/invalid ETA</span>
      <span>{past} past ETA</span>
      <span>{later} beyond 7 days</span>
    </div>
    {voyages.length > 5 && <div className='text-muted'>Showing 5 of {voyages.length} arrivals in this window.</div>}
    <button type='button' aria-label='Open Voyages to review saved ETAs' className='btn btn-sm btn-light-primary mt-3' onClick={() => navigate('/operations/voyages')}>Go to Voyages</button>
  </div></div>
}
export default VoyageTimeline
